import { useEffect, useState } from 'react'
import { getCasinoProviderToken } from '../adapters/casino'
import { setHub88URL } from '../utils/casinoProviders/hub88URL'
import { setOryxURL } from '../utils/casinoProviders/oryxURL'
import { setRelaxURL } from '../utils/casinoProviders/relaxURL'
import { setSmartsoftURL } from '../utils/casinoProviders/smartsotfURL'
import { useStateDebounce } from './useStateDebounce'
import { CASINO_PROVIDERS, DIRECTLY_INTEGRATED_CASINO_PROVIDERS } from '../utils/constants'
import { setDirectIntegrationURL } from '../utils/casinoProviders/directIntegrationURL'
import { setPlaytechURL } from '../utils/casinoProviders/playtechURL'
import { getWebsiteUrl } from '../utils/getWebsiteUrl'

const { ORYX, RELAX, SMARTSOFT, HUB88, PLAYTECH } = CASINO_PROVIDERS
const locale = process.env.GATSBY_INTL_LOCALE

const useGameProviderUrl = (
  casinoProvider,
  desktopSlug,
  mobileSlug,
  isLoggedIn,
  isMobile,
  casinoSlug,
  skin,
  slug,
  isMock,
  params,
  depositSlug,
  user,
  providerSlug
) => {
  const [providerData, setProviderData] = useState({})
  const [gameSrc, setGameSrc] = useState('')
  const [playerId, setPlayerId] = useState(null)
  const [loggedIn, setLoggedIn] = useState(null)
  const debouncedGameSrc = useStateDebounce(gameSrc, 700)
  const debouncedLoggedIn = useStateDebounce(loggedIn, 500)
  const websiteURL = getWebsiteUrl()
  const depositUrl = `${websiteURL}${depositSlug}`
  const lobbyUrl = `${websiteURL}${casinoSlug}`
  const isDirectlyIntegrated =
    Object.values(DIRECTLY_INTEGRATED_CASINO_PROVIDERS).includes(casinoProvider)

  useEffect(() => {
    setLoggedIn(isLoggedIn)
  }, [isLoggedIn])

  useEffect(() => {
    if (playerId !== user?.id) {
      setPlayerId(user?.id)
    }
  }, [user])

  const fetchGameToken = async () => {
    const { ok, data } = await getCasinoProviderToken(casinoProvider)
    if (ok) {
      const { token, rcInterval, rcElapsedTime } = data
      setProviderData({
        token,
        rcInterval,
        rcElapsedTime,
      })
    }
  }

  useEffect(() => {
    if (
      isLoggedIn &&
      !providerData?.token &&
      casinoProvider &&
      casinoProvider !== HUB88 &&
      !isDirectlyIntegrated
    ) {
      fetchGameToken()
    }
  }, [casinoProvider, isLoggedIn, providerData])

  const getGameSrc = async () => {
    switch (casinoProvider) {
      case ORYX:
        return setOryxURL({
          isLoggedIn,
          params,
          providerData,
          isMobile,
          mobileSlug,
          desktopSlug,
          casinoProvider,
          providerSlug,
          isMock,
          depositUrl,
        })
      case RELAX:
        return setRelaxURL({
          isLoggedIn,
          params,
          providerData,
          isMobile,
          desktopSlug,
          casinoProvider,
          isMock,
        })
      case SMARTSOFT:
        return setSmartsoftURL({
          providerData,
          isMobile,
          desktopSlug,
          mobileSlug,
          casinoSlug,
          slug,
          skin,
          casinoProvider,
          isMock,
        })
      case HUB88:
        return setHub88URL({
          slug,
          isMobile,
          desktopSlug,
          mobileSlug,
          isLoggedIn,
          depositUrl,
          casinoProvider,
          isMock,
          playerId,
          providerSlug,
        })
      case PLAYTECH:
        return setPlaytechURL({
          providerData,
          isMobile,
          casinoSlug,
          desktopSlug,
          mobileSlug,
          isMock,
          depositUrl,
          user,
        })
      default:
        return setDirectIntegrationURL({
          casinoProvider,
          desktopSlug,
          mobileSlug,
          depositUrl,
          lobbyUrl,
          isMobile,
          isLoggedIn,
          locale,
        })
    }
  }
  useEffect(() => {
    if (debouncedLoggedIn === null) return

    const isFunMode = !debouncedLoggedIn || params?.playMode === 'FUN'

    if (!isDirectlyIntegrated) {
      if (
        (!isFunMode && !providerData?.token && casinoProvider !== HUB88) ||
        (casinoProvider === SMARTSOFT && !casinoSlug)
      )
        return
    }

    const fetchGameSrc = async () => {
      const url = await getGameSrc()
      setGameSrc(url)
    }

    fetchGameSrc()
  }, [
    providerData,
    playerId,
    isMock,
    casinoProvider,
    isLoggedIn,
    debouncedLoggedIn,
    isMobile,
    casinoSlug,
    desktopSlug,
  ])

  return debouncedGameSrc
}

export default useGameProviderUrl
